<template>
  <div class="container mt-4">
    <TopNav>
      <NavItem
        icon="far fa-save"
        :text="$t('GENERAL.BUTTON.SAVE')"
        :pulse="isDirty"
        :loading="inProgress"
        @click="update"
      />
    </TopNav>
    <div class="mt-4">
      <h1 class="mt-4 mb-4">
        {{ $t('CONTRACTS.TITLES.EDIT_CONTRACT_TEMPLATE') }}
      </h1>
    </div>
    <div v-if="isLoading" class="mt-4 text-center">
      <HMSpinner />
    </div>
    <div v-else>
      <ValidationObserver ref="form">
        <HMTextInput
          type="text"
          rules="required"
          :label="$t('CONTRACTS.LABELS.TITLE')"
          v-model="contract.title"
          name="name"
        />
        <div
          v-if="showContractTemplateTypeSelector && !templateType"
          class="d-flex flex-row flex-nowrap"
        >
          <section class="selection-card mr-2" @click="templateType = 'type'">
            <i class="fal fa-paste fa-2x c-brand-color"></i>
            <h4 class="mt-3">
              {{ $t('CONTRACTS.TEXT.PASTE_OR_TYPE_YOUR_TEMPLATE_CONTENT') }}
            </h4>
          </section>
          <section class="selection-card ml-2" @click="templateType = 'upload'">
            <i class="fal fa-file-pdf fa-2x c-brand-color"></i>
            <h4 class="mt-3">
              {{ $t('CONTRACTS.TEXT.UPLOAD_YOUR_TEMPLATE_PDF') }}
            </h4>
          </section>
        </div>
        <div v-else>
          <HMVueEditor
            v-if="contract.content || templateType === 'type'"
            id="hm-vue-contract-template-editor"
            class="w-100 h-100"
            v-model="contract.content"
            placeholder="Your contract template content"
          />
          <HMFileUpload
            v-if="contract.document || templateType === 'upload'"
            class="w-100"
            v-model="contract.document"
            accept="application/pdf"
            @input="file => (file ? '' : (templateType = null))"
          />
        </div>
        <div class="mt-3">
          <HMVueEditor
            id="hm-vue-contract-template-editor-signature-paragraph"
            class="w-100 mt-4"
            v-model="contract.signature_paragraph"
            placeholder="Optional paragraph that will be added above the signatures"
          />
          <HMTextInput
            class="mt-3"
            type="text"
            rules="required"
            :label="$t('CONTRACTS.LABELS.YOUR_FULL_NAME')"
            v-model="contract.signature_full_name"
            name="name"
          />
          <div
            class="mt-3 d-flex flex-row flex-nowrap justify-content-between align-items-center"
          >
            <span>{{ $t('CONTRACTS.LABELS.SIGNATURE') }}</span>
            <HMButton @click="clearSignature">
              <span class="c-brand-color">{{
                $t('GENERAL.BUTTON.CLEAR')
              }}</span>
            </HMButton>
          </div>
          <div v-show="!contract.signature" class="signature-container mt-2">
            <VueSignaturePad width="100%" height="200px" ref="signaturePad" />
          </div>
          <div v-show="contract.signature">
            <img class="img-fluid" :src="contract.signature" />
          </div>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import _, { get } from 'lodash';
import DirtyMixin from '@/web/mixins/DirtyMixin';
import { errorToMessage } from '@/core/utils/errors';
import { ValidationObserver } from 'vee-validate';
import HMVueEditor from '@/web/components/common/HMVueEditor';
import HMFileUpload from '@/web/components/common/HMFileUpload';
import { formatObjectForBackend } from '@/core/api/utils/converter';

export default Vue.extend({
  name: 'EditContract',
  mixins: [DirtyMixin],
  components: {
    TopNav,
    NavItem,
    HMFileUpload,
    ValidationObserver,
    HMVueEditor,
  },
  props: {},
  data() {
    return {
      templateType: null,
      contract: {
        title: '',
        document: null,
        full_name: null,
        content: null,
        signature: null,
        signature_paragraph: null,
      },
      formError: null,
      inProgress: false,
      isLoading: true,
    };
  },
  computed: {
    showContractTemplateTypeSelector() {
      if (!this.contract) return false;
      return !this.contract.content && !this.contract.document;
    },
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        this.contract = await this.$store.dispatch(
          'contracts/getContract',
          this.$route.params.id
        );
        if (this.contract?.content?.length) {
          this.templateType = 'type';
        }
        if (this.contract?.document) {
          this.templateType = 'upload';
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(
            error.response,
            'Failed to load contract template'
          ),
          duration: -1,
        });
      } finally {
        this.isLoading = false;
      }
    },
    clearSignature() {
      this.$refs.signaturePad.clearSignature();
      this.contract.signature = null;
      this.$nextTick(() => this.$refs.signaturePad.resizeCanvas());
    },
    async update() {
      const signature = this.$refs.signaturePad.saveSignature();
      console.log(signature);
      if (!signature.isEmpty) {
        this.contract.signature = signature.data;
      }
      try {
        this.inProgress = true;
        const data = _.cloneDeep(this.contract);
        this.contract = await this.$store.dispatch(
          'contracts/updateContract',
          formatObjectForBackend(data)
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'CONTRACTS.NOTIFICATIONS.CONTRACT_TEMPLATE_SUCCESSFULLY_UPDATED'
          ),
        });
        this.$nextTick(() => this.clearDirty());
      } catch (error) {
        this.formError = get(error, 'response.data.non_field_errors[0]', null);
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(
            error.response,
            'Failed to create new contract template'
          ),
          duration: -1,
        });
      } finally {
        this.inProgress = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
  watch: {
    includeOwnSignature: {
      handler: function (value) {
        // workaround to make the signature appear, otherwise it's just blank when drawing
        setTimeout(() => {
          this.$refs.signaturePad.resizeCanvas();
        }, 100);
      },
    },
    contract: {
      handler(newContract, oldContract) {
        if (oldContract) {
          this.setDirty();
        }
      },
      deep: true,
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.selection-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem 2rem;
  border: 1px solid $light-gray;
  max-width: 50%;
  text-align: center;
  cursor: pointer;
  border-radius: $border-radius;
  &:hover {
    background-color: darken(white, 3%);
  }
}

.signature-container {
  border: 1px dashed $dark-gray;
  border-radius: $border-radius;
}
</style>
